import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { adminLogin } from "../../api/adminClient";
import { setAdminToken, setAdminRefreshToken, setAdminUser, getAdminToken, updateAdminLastActivity } from "../../utils/adminAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

const DEMO_CREDENTIALS = {
  email: "admin@endosurgery.com.my",
  password: "Admin@123",
};

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/dashboard";

  useEffect(() => {
    if (getAdminToken()) {
      navigate(from, { replace: true });
    }
  }, [navigate, from]);

  const validateEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const isFormValid = useMemo(
    () => email.trim() !== "" && password.trim() !== "" && password.length >= 8,
    [email, password]
  );

  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setError("");
    setEmailError("");
    setPasswordError("");

    if (!email?.trim()) {
      setEmailError("Please enter your email");
      return;
    }
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }
    if (!validateEmail(email.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      if (data?.token) {
        setAdminToken(data.token);
        if (data?.refresh_token) setAdminRefreshToken(data.refresh_token);
        setAdminUser(data.user ?? null);
        updateAdminLastActivity();
        navigate(from, { replace: true });
      } else {
        setError("Invalid response from server.");
      }
    } catch (err) {
      const msg = err?.message || "Login failed. Check your credentials.";
      const code = err?.message_code;
      if (code === "EMAIL_NOT_FOUND") {
        setEmailError(msg);
      } else if (code === "INVALID_PASSWORD") {
        setPasswordError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-layout min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md" hover={false} animation={false}>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 flex items-center justify-center">
              <img src="/assets/images/Endo-Logo-small.png" alt="Endosurgery Logo" className="w-full h-full object-contain" />
            </div>
          </div>
          <CardTitle className="text-2xl">Admin Login</CardTitle>
          <CardDescription>Enter your credentials to access the admin panel</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError("");
                if (error) setError("");
              }}
              placeholder="Enter your email"
              required
              autoFocus
              disabled={loading}
              error={emailError}
            />

            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium text-foreground">Password</label>
              </div>
              <Input
                label=""
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError("");
                  if (error) setError("");
                }}
                placeholder="Enter password (min 8 characters)"
                required
                disabled={loading}
                error={passwordError}
                description={password && password.length < 8 ? "Password must be at least 8 characters" : ""}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
              >
                <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
              </button>
            </div>

            <Button
              type="submit"
              className="w-full"
              loading={loading}
              disabled={loading || !isFormValid}
              fullWidth
            >
              Sign In
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
              <Icon name="Shield" size={14} />
              <span>Secure admin access only</span>
            </div>

            <div className="bg-muted/50 rounded-md p-4 border border-border">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="Info" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">Demo Credentials</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEmail(DEMO_CREDENTIALS.email);
                    setPassword(DEMO_CREDENTIALS.password);
                    setError("");
                    setEmailError("");
                    setPasswordError("");
                  }}
                  className="h-7 text-xs"
                >
                  Fill Demo
                </Button>
              </div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Email:</span>
                  <code className="bg-background px-2 py-1 rounded text-primary font-mono">
                    {DEMO_CREDENTIALS.email}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Password:</span>
                  <code className="bg-background px-2 py-1 rounded text-primary font-mono">
                    {DEMO_CREDENTIALS.password}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
